import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { adminApi } from '../lib/api';
import VisitStatsCards from '../components/admin/VisitStatsCards';
import {
  Library, Star, Layers, Inbox, MessageSquare, Mail, ArrowRight, ShieldCheck, Users,
} from 'lucide-react';

const adminLinks = [
  {
    to: '/admin/users',
    icon: Users,
    title: 'Users',
    description: 'Registered users, login activity, suspensions and deletions.',
    testId: 'admin-users-link',
  },
  {
    to: '/admin/content',
    icon: Library,
    title: 'Content Library',
    description: 'Create and publish Articles and Tool & Simulator User Guides.',
    testId: 'admin-content-library-link',
  },
  {
    to: '/admin/articles',
    icon: Star,
    title: 'Articles',
    description: 'Manage the article cards and sections on the public Articles page.',
    testId: 'admin-featured-articles-link',
  },
  {
    to: '/admin/how-it-works',
    icon: Layers,
    title: 'How It Works Topics',
    description: 'Manage the "How Does It Work?" topic library.',
    testId: 'admin-how-it-works-link',
  },
  {
    to: '/admin/topic-requests',
    icon: Inbox,
    title: 'Topic Requests',
    description: 'Review topics readers have asked to see covered.',
    testId: 'admin-topic-requests-link',
  },
  {
    to: '/admin/feedback',
    icon: MessageSquare,
    title: 'Feedback',
    description: 'Review site feedback submitted by users.',
    testId: 'admin-feedback-link',
  },
  {
    to: '/admin/community',
    icon: ShieldCheck,
    title: 'Community Moderation',
    description: 'Review questions and answers reported by users.',
    testId: 'admin-community-link',
  },
  {
    to: '/admin/newsletter-subscribers',
    icon: Mail,
    title: 'Newsletter Subscribers',
    description: 'View everyone who signed up for the newsletter.',
    testId: 'admin-newsletter-subscribers-link',
  },
];

const AdminDashboard = () => {
  const [openReports, setOpenReports] = useState(0);

  useEffect(() => {
    adminApi.getCommunityOpenReportCount()
      .then((data) => setOpenReports(data.open))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="font-chivo text-3xl font-black uppercase tracking-tight">Admin</h1>
          <p className="text-muted-foreground">Manage site content and users</p>
        </div>

        <VisitStatsCards />

        <div className="grid sm:grid-cols-2 gap-4">
          {adminLinks.map(({ to, icon: Icon, title, description, testId }) => (
            <Link key={to} to={to} data-testid={testId}>
              <Card className="border-2 technical-card h-full hover:border-autoxium-blue transition-colors">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-md bg-chassis flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-autoxium-blue" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-chivo font-bold uppercase tracking-tight mb-1 flex items-center gap-2">
                      {title}
                      <ArrowRight className="h-4 w-4" />
                      {to === '/admin/community' && openReports > 0 && (
                        <span
                          className="ml-auto rounded-full bg-destructive text-white text-[10px] font-mono px-2 py-0.5"
                          data-testid="open-reports-badge"
                        >
                          {openReports} open
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
